import { Box, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { DripAlert } from "../../hooks/useDripHistory";
import { DRIP_TEXT_MUTED } from "./dripStyles";

interface DripAlertsProps {
  alerts: DripAlert[];
  // Resolve a mint to a human-readable token symbol.
  resolveSymbol: (mint: string) => string;
}

// Drip alerts are always informational — a reward paid in kySOL still compounds
// in the user's position, so there's nothing for them to fix or worry about.
const INFO_STYLE = {
  bg: "rgba(95, 244, 244, 0.06)",
  border: "rgba(95, 244, 244, 0.3)",
  iconColor: "#5FF4F4",
};

const formatSince = (iso: string): string | null => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

interface ResolvedAlert {
  message: string;
}

// Render strictly from the alert type + paidAsKySol flag — never key off paidAsKySol
// alone, since a user who deliberately chose kySOL has paidAsKySol history but no alert.
const resolveAlert = (alert: DripAlert, symbol: string): ResolvedAlert | null => {
  switch (alert.type) {
    case "reward_token_unroutable":
      return {
        message: `Your reward token (${symbol}) can't currently be swapped, so this period's reward was paid to you in kySOL instead.`,
      };
    // Temporarily hidden — falls through to `default` so no alert is rendered.
    // case "reward_below_min_swap":
    //   return {
    //     message: `Your reward this period was too small to swap into ${symbol}, so it was paid in kySOL.`,
    //   };
    case "reward_below_min_transfer":
      return {
        // The reward wasn't lost or held back — it was paid in kySOL and compounds
        // in the user's position (see the "Why was my reward paid in kySOL?" FAQ).
        message: "Your reward this period was too small to pay out, so it was paid in kySOL and compounds in your position.",
      };
    default:
      return null;
  }
};

const alertTime = (alert: DripAlert): number => {
  const time = new Date(alert.lastSeen).getTime();
  return Number.isNaN(time) ? 0 : time;
};

export const DripAlerts = ({ alerts, resolveSymbol }: DripAlertsProps) => {
  if (!alerts || alerts.length === 0) return null;

  // Only ever surface the most recent alert — a user has one chosen reward token at a
  // time, so the latest entry reflects the current state of their payouts.
  const latest = [...alerts].sort((a, b) => alertTime(b) - alertTime(a))[0];
  const resolved = resolveAlert(latest, resolveSymbol(latest.mint));
  if (!resolved) return null;

  const since = formatSince(latest.since);

  return (
    <Box
      p={3}
      borderRadius="xl"
      border="1px solid"
      borderColor={INFO_STYLE.border}
      bg={INFO_STYLE.bg}
    >
      <HStack spacing={3} align="flex-start">
        <Icon
          as={InfoOutlineIcon}
          color={INFO_STYLE.iconColor}
          boxSize={4}
          mt="2px"
          flexShrink={0}
        />
        <VStack spacing={1} align="stretch">
          <Text fontSize="sm" color="white">
            {resolved.message}
          </Text>
          {since && (
            <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
              Ongoing since {since}
            </Text>
          )}
        </VStack>
      </HStack>
    </Box>
  );
};
